import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Lock, ArrowRight, CheckCircle, ShieldCheck } from "lucide-react";
import logo from "@/assets/velocity-logo-light.png";

const CLIENT_PORTAL_ENTRY_KEY = "client-portal-entry";

const ChangePassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const getRedirect = async () => {
    if (!user) {
      sessionStorage.removeItem(CLIENT_PORTAL_ENTRY_KEY);
      return "/client-login";
    }

    const [{ data: isAdmin }, { data: isEmployee }, { data: isAuditor }, { data: isClient }] = await Promise.all([
      supabase.rpc("has_role", { _user_id: user.id, _role: "admin" }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "employee" }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "auditor" as any }),
      supabase.rpc("has_role", { _user_id: user.id, _role: "client" }),
    ]);

    // Role-based routing ALWAYS wins over the entry-point hint.
    // Admins must land in /admin regardless of which login page they used.
    const cameFromClientPortal = sessionStorage.getItem(CLIENT_PORTAL_ENTRY_KEY) === "true";
    sessionStorage.removeItem(CLIENT_PORTAL_ENTRY_KEY);

    if (isAdmin) return "/admin";
    if (isAuditor) return "/auditor";
    if (isEmployee) return "/employee";
    if (isClient) return "/portal";
    return cameFromClientPortal ? "/portal" : "/client-login";
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    const { error: updateErr } = await supabase.auth.updateUser({
      password,
      data: { must_change_password: false },
    });

    if (updateErr) {
      setError(updateErr.message);
    } else {
      setSuccess(true);
      const destination = await getRedirect();
      setTimeout(() => navigate(destination), 1500);
    }
    setLoading(false);
  };

  return (
    <Layout>
      <div
        className="min-h-[80vh] flex items-center justify-center relative overflow-hidden"
        style={{
          background:
            "linear-gradient(160deg, hsl(218 55% 10%) 0%, hsl(160 30% 14%) 50%, hsl(215 35% 26%) 100%)",
        }}
      >
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-bl from-lime/8 to-transparent rounded-bl-full" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-teal/5 to-transparent rounded-tr-full" />

        <div className="relative z-10 w-full max-w-md mx-6 py-20">
          <div className="text-center mb-10">
            <a href="/">
              <img
                src={logo}
                alt="Velocity Offshore"
                className="h-14 mx-auto mb-6 opacity-90"
              />
            </a>
            <div className="flex items-center justify-center gap-2 mb-4">
              <ShieldCheck className="w-6 h-6 text-lime" />
              <h1 className="font-display text-3xl text-primary-foreground tracking-tight">
                Set Your <span className="text-lime">Password</span>
              </h1>
            </div>
            <p className="text-primary-foreground/40 font-body text-sm">
              For security, please create a new password to replace your
              temporary one.
            </p>
          </div>

          <div className="glass-card p-8 rounded-2xl">
            {success ? (
              <div className="text-center py-4">
                <CheckCircle className="w-12 h-12 text-lime mx-auto mb-4" />
                <p className="text-primary-foreground font-body text-sm">
                  Password updated successfully! Redirecting…
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">
                    New Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-lime/50" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-lime/40 focus:ring-1 focus:ring-lime/20 transition-all"
                      placeholder="Minimum 8 characters"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[11px] font-bold tracking-[2px] uppercase text-primary-foreground/50 mb-2 font-body">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-lime/50" />
                    <input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full pl-11 pr-4 py-3.5 bg-primary-foreground/5 border border-primary-foreground/10 rounded-xl text-primary-foreground font-body text-sm placeholder:text-primary-foreground/25 focus:outline-none focus:border-lime/40 focus:ring-1 focus:ring-lime/20 transition-all"
                      placeholder="Re-enter your new password"
                      required
                    />
                  </div>
                </div>

                {error && (
                  <div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm font-body">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-gradient-to-r from-lime to-lime-dark text-navy-deep text-[12px] font-bold tracking-[1.5px] uppercase rounded-xl hover:shadow-xl hover:shadow-lime/25 transition-all duration-300 font-body disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Updating…" : "Set New Password"}
                  {!loading && <ArrowRight className="w-4 h-4" />}
                </button>
              </form>
            )}
          </div>

          <p className="text-center text-primary-foreground/20 text-xs font-body mt-6">
            You must change your temporary password before continuing.
          </p>
        </div>
      </div>
    </Layout>
  );
};

export default ChangePassword;
